import { CAPTAINS, FACILITIES, RESOURCE_LABELS, STAGES, getFacilityCost, getModeLabel, getRunModifiers, getStage } from '../game/content';
import { addResources, createDefaultSave, exportSave, importSave, loadSave, persistSave } from '../game/save';
import type { CaptainId, FacilityId, GameMode, RunResult, SaveData } from '../game/types';
import { createRunGame } from '../phaser/createGame';
import { GameAudio } from './audio';

type Screen = 'title' | 'base' | 'missions' | 'captains' | 'settings' | 'briefing' | 'game' | 'result';

interface HudDetail {
  troops: number;
  progress: number;
  damage: number;
  rate: number;
  drones: number;
  shield: boolean;
  focus: number;
  bossHp: number | null;
}

interface ToastDetail {
  message: string;
  tone: 'positive' | 'danger';
}

export class App {
  private readonly root: HTMLElement;
  private save: SaveData;
  private screen: Screen = 'title';
  private mode: GameMode = 'normal';
  private selectedStageId = 1;
  private result: RunResult | null = null;
  private game: ReturnType<typeof createRunGame> | null = null;
  private toastTimer = 0;
  private readonly audio = new GameAudio();
  private finishHandler = (event: Event): void => this.finishRun((event as CustomEvent<RunResult>).detail);
  private restartHandler = (): void => this.launchRun();
  private pauseHandler = (event: Event): void => this.updatePause((event as CustomEvent<{ paused: boolean }>).detail.paused);
  private hudHandler = (event: Event): void => this.updateHud((event as CustomEvent<HudDetail>).detail);
  private toastHandler = (event: Event): void => this.showToast((event as CustomEvent<ToastDetail>).detail);

  constructor(root: HTMLElement) {
    this.root = root;
    this.save = loadSave();
    this.audio.setEnabled(this.save.settings.sound);
    window.addEventListener('breakline:finish', this.finishHandler);
    window.addEventListener('breakline:restart', this.restartHandler);
    window.addEventListener('breakline:pause', this.pauseHandler);
    window.addEventListener('breakline:hud', this.hudHandler);
    window.addEventListener('breakline:toast', this.toastHandler);
    this.render();
  }

  private persist(): void {
    persistSave(this.save);
  }

  private render(): void {
    this.root.dataset.screen = this.screen;
    if (this.screen === 'title') this.renderTitle();
    if (this.screen === 'base') this.renderBase();
    if (this.screen === 'missions') this.renderMissions();
    if (this.screen === 'captains') this.renderCaptains();
    if (this.screen === 'settings') this.renderSettings();
    if (this.screen === 'briefing') this.renderBriefing();
    if (this.screen === 'game') this.renderGame();
    if (this.screen === 'result') this.renderResult();
    this.attachCommonActions();
  }

  private go(screen: Screen): void {
    this.audio.play('click');
    this.screen = screen;
    this.render();
  }

  private renderTitle(): void {
    this.root.innerHTML = [
      '<main class="title-screen key-art-screen">',
      '  <div class="key-art-overlay"></div>',
      '  <div class="title-content">',
      '    <div class="eyebrow"><span class="status-dot"></span> ORION OUTPOST // ONLINE</div>',
      '    <div class="title-mark"><span>BREAK</span><strong>LINE</strong></div>',
      '    <p class="title-sub">LAST SHELTER</p>',
      '    <p class="title-copy">選べ。撃て。仲間を増やせ。<br>夜明けまで、最後の防衛線を守り抜け。</p>',
      '    <div class="title-actions">',
      '      <button class="primary-button xl" data-action="enter-base"><span>基地へ入る</span><b>→</b></button>',
      '      <button class="ghost-button" data-nav="settings">設定</button>',
      '    </div>',
      '    <p class="local-save-note">● 進行状況はこのブラウザ内に保存されます</p>',
      '  </div>',
      '  <div class="title-build">MVP BUILD 0.1</div>',
      '</main>',
    ].join('');
  }

  private renderResourceBar(): string {
    const resources = this.save.resources;
    return [
      '<div class="resource-bar" aria-label="所持資源">',
      this.resourceChip('食料', resources.food, 'food'),
      this.resourceChip('金属', resources.metal, 'metal'),
      this.resourceChip('電子', resources.electronics, 'electronics'),
      this.resourceChip('設計図', resources.blueprints, 'blueprints'),
      '</div>',
    ].join('');
  }

  private resourceChip(label: string, value: number, type: string): string {
    return '<div class="resource-chip ' + type + '"><span class="resource-icon"></span><small>' + label + '</small><strong>' + value.toLocaleString('ja-JP') + '</strong></div>';
  }

  private renderBase(): void {
    const captain = CAPTAINS[this.save.captainId];
    const totalStars = Object.values(this.save.stageStars).reduce((sum, value) => sum + value, 0);
    const cards = (Object.keys(FACILITIES) as FacilityId[]).map((id) => {
      const facility = FACILITIES[id];
      const level = this.save.facilities[id];
      const cost = getFacilityCost(id, level);
      const resource = Object.keys(cost)[0] as keyof typeof cost;
      const amount = cost[resource] ?? 0;
      const maxed = level >= 5;
      const hqBlocked = id !== 'hq' && level >= this.save.facilities.hq;
      const affordable = (this.save.resources[resource] ?? 0) >= amount;
      const disabled = maxed || hqBlocked || !affordable;
      const reason = maxed ? 'MAX' : hqBlocked ? '司令部Lvが必要' : RESOURCE_LABELS[resource] + ' ' + amount;
      return [
        '<article class="facility-card facility-' + id + ' ' + (id === 'hq' ? 'featured' : '') + '">',
        '  <div class="facility-art facility-art-' + id + '" role="img" aria-label="' + facility.name + 'の外観"></div>',
        '  <div class="facility-copy"><small>' + facility.name + '</small><h3>' + facility.shortName + '</h3><p>' + facility.description + '</p></div>',
        '  <div class="level-meter"><span>LV</span><strong>' + level + '</strong><i style="--level:' + level + '"></i></div>',
        '  <button class="upgrade-button" data-upgrade="' + id + '" ' + (disabled ? 'disabled' : '') + '>' + reason + '</button>',
        '</article>',
      ].join('');
    }).join('');

    this.root.innerHTML = [
      '<main class="base-screen key-art-screen">',
      '  <div class="key-art-overlay base-overlay"></div>',
      '  <header class="top-shell"><div><small>ORION OUTPOST</small><h1>第' + this.save.facilities.hq + '防衛基地</h1></div><button class="icon-button" data-nav="settings" aria-label="設定">⚙</button></header>',
      this.renderResourceBar(),
      '  <section class="base-status">',
      '    <div><span class="status-kicker">総合状況</span><strong>' + totalStars + '<small> STARS</small></strong><p>到達地点 STAGE ' + this.save.unlockedStage + ' / 15</p></div>',
      '    <div class="captain-mini" style="--captain:' + captain.accent + '"><span class="captain-art captain-art-' + this.save.captainId + '" role="img" aria-label="' + captain.name + '"></span><div><small>指揮官</small><b>' + captain.name + '</b></div></div>',
      '  </section>',
      '  <section class="facility-grid" aria-label="基地施設">' + cards + '</section>',
      '  <nav class="base-actions">',
      '    <button class="secondary-button" data-nav="captains"><span>隊長</span><strong>' + captain.callsign + '</strong></button>',
      '    <button class="deploy-button" data-nav="missions"><small>NEXT OPERATION</small><span>出 撃</span><b>→</b></button>',
      '  </nav>',
      '</main>',
    ].join('');
  }

  private renderMissions(): void {
    const modeTabs: GameMode[] = ['normal', 'hard', 'daily'];
    const tabs = modeTabs.map((mode) =>
      '<button class="mode-tab ' + (this.mode === mode ? 'active' : '') + '" data-mode="' + mode + '">' + getModeLabel(mode) + '</button>',
    ).join('');
    const stages = this.mode === 'daily' ? [STAGES[(new Date().getUTCDate() - 1) % STAGES.length]] : STAGES;
    const cards = stages.map((stage) => {
      const key = this.mode + ':' + stage.id;
      const stars = this.save.stageStars[key] ?? 0;
      const normalStars = this.save.stageStars['normal:' + stage.id] ?? 0;
      const locked =
        this.mode === 'normal'
          ? stage.id > this.save.unlockedStage
          : this.mode === 'hard'
            ? normalStars < 1
            : false;
      return [
        '<button class="mission-card ' + (locked ? 'locked' : '') + '" data-stage="' + stage.id + '" ' + (locked ? 'disabled' : '') + ' style="--stage-accent:' + stage.accent + '">',
        '  <span class="mission-index">' + String(stage.id).padStart(2, '0') + '</span>',
        '  <span class="mission-copy"><small>CHAPTER ' + stage.chapter + ' — ' + String(stage.order).padStart(2, '0') + '</small><strong>' + stage.name + '</strong><em>' + stage.subtitle + '</em></span>',
        '  <span class="mission-stars" aria-label="' + stars + 'つ星">' + [1, 2, 3].map((star) => star <= stars ? '★' : '☆').join('') + '</span>',
        '  <span class="mission-state">' + (locked ? 'LOCKED' : 'DEPLOY →') + '</span>',
        '</button>',
      ].join('');
    }).join('');

    this.root.innerHTML = [
      '<main class="panel-screen missions-screen">',
      '  <header class="screen-header"><button class="back-button" data-nav="base">←</button><div><small>OPERATION MAP</small><h1>出撃地点を選択</h1></div></header>',
      this.renderResourceBar(),
      '  <div class="mode-tabs">' + tabs + '</div>',
      this.mode === 'daily' ? '<div class="daily-brief">本日の固定シード作戦。全プレイヤーが同じ配置に挑戦します。</div>' : '',
      '  <section class="mission-list">' + cards + '</section>',
      '</main>',
    ].join('');
  }

  private renderCaptains(): void {
    const cards = (Object.keys(CAPTAINS) as CaptainId[]).map((id) => {
      const captain = CAPTAINS[id];
      const selected = this.save.captainId === id;
      return [
        '<button class="captain-card ' + (selected ? 'selected' : '') + '" data-captain="' + id + '" style="--captain:' + captain.accent + '">',
        '  <span class="portrait captain-art captain-art-' + id + '" aria-hidden="true"></span>',
        '  <span class="captain-copy"><small>' + captain.callsign + ' // ' + captain.role + '</small><strong>' + captain.name + '</strong><em>' + captain.description + '</em><span class="passive">◆ ' + captain.passive + '</span></span>',
        '  <span class="select-state">' + (selected ? '選択中' : '選択') + '</span>',
        '</button>',
      ].join('');
    }).join('');
    this.root.innerHTML = [
      '<main class="panel-screen captain-screen">',
      '  <header class="screen-header"><button class="back-button" data-nav="base">←</button><div><small>COMMAND ROSTER</small><h1>隊長を選択</h1></div></header>',
      '  <p class="screen-lead">隊長の能力はすべての出撃へ適用されます。作戦に合う1人を選んでください。</p>',
      '  <section class="captain-list">' + cards + '</section>',
      '</main>',
    ].join('');
  }

  private renderSettings(): void {
    this.root.innerHTML = [
      '<main class="panel-screen settings-screen">',
      '  <header class="screen-header"><button class="back-button" data-action="settings-back">←</button><div><small>SYSTEM</small><h1>設定・データ</h1></div></header>',
      '  <section class="settings-panel">',
      this.toggleRow('sound', 'サウンド', '操作音と警告音を再生します', this.save.settings.sound),
      this.toggleRow('reducedMotion', '演出を軽減', '画面揺れや強いフラッシュを抑えます', this.save.settings.reducedMotion),
      '  </section>',
      '  <section class="data-panel"><div><small>LOCAL SAVE</small><h2>このブラウザに保存中</h2><p>別端末への移行やブラウザデータ削除に備えて、セーブを書き出せます。</p></div>',
      '    <div class="data-actions"><button class="secondary-button" data-action="export-save">JSONを書き出す</button><label class="secondary-button file-button">JSONを読み込む<input type="file" accept="application/json" data-action="import-save"></label></div>',
      '  </section>',
      '  <button class="danger-button" data-action="reset-save">進行状況を初期化</button>',
      '  <p class="version-note">BREAKLINE MVP 0.1 // SAVE FORMAT v1</p>',
      '</main>',
    ].join('');
  }

  private toggleRow(id: keyof SaveData['settings'], title: string, description: string, checked: boolean): string {
    return '<label class="toggle-row"><span><strong>' + title + '</strong><small>' + description + '</small></span><input type="checkbox" data-setting="' + id + '" ' + (checked ? 'checked' : '') + '><i></i></label>';
  }

  private renderBriefing(): void {
    const stage = getStage(this.selectedStageId);
    const captain = CAPTAINS[this.save.captainId];
    const modifiers = getRunModifiers(this.save);
    this.root.innerHTML = [
      '<main class="briefing-screen" style="--stage-accent:' + stage.accent + '">',
      '  <div class="briefing-grid"></div>',
      '  <button class="back-button briefing-back" data-nav="missions">←</button>',
      '  <section class="briefing-card">',
      '    <div class="briefing-index">' + String(stage.id).padStart(2, '0') + '</div>',
      '    <small>CHAPTER ' + stage.chapter + ' // ' + getModeLabel(this.mode) + '</small>',
      '    <h1>' + stage.name + '</h1>',
      '    <p>' + stage.subtitle + '</p>',
      '    <div class="briefing-threat"><span>最終目標</span><strong>' + stage.bossName + '</strong></div>',
      '    <div class="loadout-row"><div class="captain-avatar captain-art captain-art-' + this.save.captainId + '" style="--captain:' + captain.accent + '" role="img" aria-label="' + captain.name + '"></div><div><small>隊長</small><strong>' + captain.name + '</strong><em>' + captain.passive + '</em></div></div>',
      '    <div class="stat-strip"><span><small>兵士</small><b>' + modifiers.startTroops + '</b></span><span><small>火力</small><b>' + modifiers.damagePerUnit.toFixed(2) + '</b></span><span><small>連射</small><b>' + modifiers.fireRate.toFixed(1) + '</b></span></div>',
      !this.save.tutorialSeen ? '<div class="control-hint"><b>操作</b><span>画面を左右にドラッグ</span><small>PC: A / D または ← / →</small></div>' : '',
      '    <button class="primary-button xl launch-button" data-action="launch"><span>作戦開始</span><b>▶</b></button>',
      '  </section>',
      '</main>',
    ].join('');
  }

  private renderGame(): void {
    const stage = getStage(this.selectedStageId);
    this.root.innerHTML = [
      '<main class="game-screen">',
      '  <div id="game-container" aria-label="ゲーム画面"></div>',
      '  <div class="game-hud">',
      '    <button class="hud-pause" data-game="pause" aria-label="一時停止">Ⅱ</button>',
      '    <div class="operation-chip"><small>' + getModeLabel(this.mode) + ' / ' + String(stage.id).padStart(2, '0') + '</small><strong>' + stage.name + '</strong></div>',
      '    <div class="troop-chip"><small>SQUAD</small><strong id="hud-troops">--</strong></div>',
      '    <div class="progress-track"><i id="hud-progress"></i></div>',
      '    <div class="buff-stack"><span id="hud-damage">DMG ×1.00</span><span id="hud-rate">RATE 4.5</span><span id="hud-special" hidden></span></div>',
      '    <div class="boss-track" id="boss-track" hidden><small>' + stage.bossName + '</small><i><b id="boss-progress"></b></i></div>',
      '    <div class="game-toast" id="game-toast" aria-live="polite"></div>',
      '    <div class="drag-hint" id="drag-hint"><span>↔</span> 左右にドラッグ</div>',
      '  </div>',
      '  <div class="pause-overlay" id="pause-overlay" hidden><div class="pause-card"><small>OPERATION PAUSED</small><h2>一時停止</h2><button class="primary-button" data-game="resume">作戦に戻る</button><button class="secondary-button" data-game="restart">最初から再挑戦</button><button class="ghost-button" data-game="quit">基地へ帰還</button></div></div>',
      '</main>',
    ].join('');
    requestAnimationFrame(() => this.mountGame());
  }

  private mountGame(): void {
    const parent = document.querySelector<HTMLElement>('#game-container');
    if (!parent) return;
    this.destroyGame();
    this.game = createRunGame(parent, {
      stage: getStage(this.selectedStageId),
      mode: this.mode,
      captainId: this.save.captainId,
      modifiers: getRunModifiers(this.save),
      settings: this.save.settings,
    });
    window.setTimeout(() => document.querySelector('#drag-hint')?.classList.add('fade'), 3200);
  }

  private renderResult(): void {
    const result = this.result;
    if (!result) {
      this.go('base');
      return;
    }
    const stage = getStage(result.stageId);
    const rewardItems = (Object.keys(result.rewards) as Array<keyof typeof result.rewards>)
      .filter((id) => result.rewards[id] > 0)
      .map((id) => '<span><small>' + RESOURCE_LABELS[id] + '</small><strong>+' + result.rewards[id] + '</strong></span>')
      .join('');
    const failureCopy = result.reason === 'quit' ? '作戦から帰還しました' : result.progress < 0.45 ? '部隊強化とゲート選択を見直そう' : 'あと少し。危険な射線を避けよう';
    this.root.innerHTML = [
      '<main class="result-screen ' + (result.victory ? 'victory' : 'defeat') + '">',
      '  <div class="result-rays"></div>',
      '  <section class="result-card">',
      '    <div class="result-emblem">' + (result.victory ? '✓' : '×') + '</div>',
      '    <small>' + (result.victory ? 'OPERATION COMPLETE' : 'OPERATION ENDED') + '</small>',
      '    <h1>' + (result.victory ? '防衛線 突破' : '部隊 壊滅') + '</h1>',
      '    <p>' + stage.name + ' — ' + (result.victory ? [1, 2, 3].map((star) => star <= result.stars ? '★' : '☆').join('') : failureCopy) + '</p>',
      '    <div class="result-stats"><span><small>残存兵士</small><b>' + result.troopsLeft + '</b></span><span><small>撃破</small><b>' + result.kills + '</b></span><span><small>進行</small><b>' + Math.round(result.progress * 100) + '%</b></span></div>',
      '    <div class="reward-row">' + rewardItems + '</div>',
      '    <div class="result-actions"><button class="primary-button" data-action="retry">再挑戦</button><button class="secondary-button" data-nav="base">基地へ戻る</button></div>',
      '  </section>',
      '</main>',
    ].join('');
  }

  private attachCommonActions(): void {
    this.root.querySelectorAll<HTMLElement>('[data-nav]').forEach((element) => {
      element.addEventListener('click', () => this.go(element.dataset.nav as Screen));
    });
    this.root.querySelector('[data-action="enter-base"]')?.addEventListener('click', () => this.go('base'));
    this.root.querySelectorAll<HTMLButtonElement>('[data-upgrade]').forEach((button) => {
      button.addEventListener('click', () => this.upgradeFacility(button.dataset.upgrade as FacilityId));
    });
    this.root.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((button) => {
      button.addEventListener('click', () => {
        this.mode = button.dataset.mode as GameMode;
        this.audio.play('click');
        this.render();
      });
    });
    this.root.querySelectorAll<HTMLButtonElement>('[data-stage]').forEach((button) => {
      button.addEventListener('click', () => {
        this.selectedStageId = Number(button.dataset.stage);
        this.go('briefing');
      });
    });
    this.root.querySelectorAll<HTMLButtonElement>('[data-captain]').forEach((button) => {
      button.addEventListener('click', () => this.selectCaptain(button.dataset.captain as CaptainId));
    });
    this.root.querySelectorAll<HTMLInputElement>('[data-setting]').forEach((input) => {
      input.addEventListener('change', () => this.changeSetting(input.dataset.setting as keyof SaveData['settings'], input.checked));
    });
    this.root.querySelector('[data-action="settings-back"]')?.addEventListener('click', () => this.go(this.save.totalRuns > 0 ? 'base' : 'title'));
    this.root.querySelector('[data-action="export-save"]')?.addEventListener('click', () => this.downloadSave());
    this.root.querySelector<HTMLInputElement>('[data-action="import-save"]')?.addEventListener('change', (event) => this.readSaveFile(event));
    this.root.querySelector('[data-action="reset-save"]')?.addEventListener('click', () => this.resetSave());
    this.root.querySelector('[data-action="launch"]')?.addEventListener('click', () => this.launchRun());
    this.root.querySelector('[data-action="retry"]')?.addEventListener('click', () => {
      this.screen = 'briefing';
      this.render();
    });
    this.root.querySelectorAll<HTMLElement>('[data-game]').forEach((button) => {
      button.addEventListener('click', () => {
        this.audio.play('click');
        window.dispatchEvent(new CustomEvent('breakline:command', { detail: { action: button.dataset.game } }));
      });
    });
  }

  private upgradeFacility(id: FacilityId): void {
    const level = this.save.facilities[id];
    if (level >= 5) return;
    if (id !== 'hq' && level >= this.save.facilities.hq) return;
    const cost = getFacilityCost(id, level);
    const resource = Object.keys(cost)[0] as keyof typeof cost;
    const amount = cost[resource] ?? 0;
    if (this.save.resources[resource] < amount) return;
    this.save.resources[resource] -= amount;
    this.save.facilities[id] += 1;
    this.persist();
    this.audio.play('reward');
    this.render();
  }

  private selectCaptain(id: CaptainId): void {
    this.save.captainId = id;
    this.persist();
    this.audio.play('reward');
    this.render();
  }

  private changeSetting(id: keyof SaveData['settings'], value: boolean): void {
    this.save.settings[id] = value;
    this.audio.setEnabled(this.save.settings.sound);
    this.persist();
    this.audio.play('click');
  }

  private launchRun(): void {
    this.destroyGame();
    this.save.tutorialSeen = true;
    this.persist();
    this.audio.play('launch');
    this.screen = 'game';
    this.render();
  }

  private finishRun(result: RunResult): void {
    if (this.screen !== 'game') return;
    this.destroyGame();
    this.result = result;
    this.save = addResources(this.save, result.rewards);
    this.save.totalRuns += 1;
    if (result.victory) {
      const key = result.mode + ':' + result.stageId;
      this.save.stageStars[key] = Math.max(this.save.stageStars[key] ?? 0, result.stars);
      if (result.mode === 'normal') this.save.unlockedStage = Math.min(15, Math.max(this.save.unlockedStage, result.stageId + 1));
      this.audio.play('reward');
    } else {
      this.audio.play('danger');
    }
    this.persist();
    this.screen = 'result';
    this.render();
  }

  private destroyGame(): void {
    if (!this.game) return;
    this.game.destroy(true);
    this.game = null;
  }

  private updateHud(detail: HudDetail): void {
    const troops = document.querySelector('#hud-troops');
    const progress = document.querySelector<HTMLElement>('#hud-progress');
    const damage = document.querySelector('#hud-damage');
    const rate = document.querySelector('#hud-rate');
    const special = document.querySelector<HTMLElement>('#hud-special');
    const bossTrack = document.querySelector<HTMLElement>('#boss-track');
    const bossProgress = document.querySelector<HTMLElement>('#boss-progress');
    if (troops) troops.textContent = String(detail.troops);
    if (progress) progress.style.width = Math.round(detail.progress * 100) + '%';
    if (damage) damage.textContent = 'DMG ×' + detail.damage.toFixed(2);
    if (rate) rate.textContent = 'RATE ' + detail.rate.toFixed(1);
    if (special) {
      const labels = [];
      if (detail.drones > 0) labels.push('DRONE ' + detail.drones);
      if (detail.shield) labels.push('SHIELD');
      if (detail.focus > 1.02) labels.push('FOCUS ×' + detail.focus.toFixed(2));
      special.textContent = labels.join(' · ');
      special.hidden = labels.length === 0;
    }
    if (bossTrack && bossProgress) {
      bossTrack.hidden = detail.bossHp === null;
      if (detail.bossHp !== null) bossProgress.style.width = Math.round(detail.bossHp * 100) + '%';
    }
  }

  private updatePause(paused: boolean): void {
    const overlay = document.querySelector<HTMLElement>('#pause-overlay');
    if (overlay) overlay.hidden = !paused;
  }

  private showToast(detail: ToastDetail): void {
    const toast = document.querySelector<HTMLElement>('#game-toast');
    if (!toast) return;
    window.clearTimeout(this.toastTimer);
    toast.textContent = detail.message;
    toast.className = 'game-toast show ' + detail.tone;
    if (detail.tone === 'danger') this.audio.play('danger');
    this.toastTimer = window.setTimeout(() => {
      toast.className = 'game-toast';
    }, 900);
  }

  private downloadSave(): void {
    const blob = new Blob([exportSave(this.save)], { type: 'application/json' });
    const anchor = document.createElement('a');
    anchor.href = URL.createObjectURL(blob);
    anchor.download = 'breakline-save.json';
    anchor.click();
    URL.revokeObjectURL(anchor.href);
    this.audio.play('reward');
  }

  private async readSaveFile(event: Event): Promise<void> {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    try {
      this.save = importSave(await file.text());
      this.audio.setEnabled(this.save.settings.sound);
      this.persist();
      this.audio.play('reward');
      this.go('base');
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'セーブデータを読み込めませんでした。');
    }
  }

  private resetSave(): void {
    if (!window.confirm('すべての進行状況を初期化します。元に戻せません。よろしいですか？')) return;
    this.save = createDefaultSave();
    this.persist();
    this.audio.setEnabled(this.save.settings.sound);
    this.go('title');
  }
}
